import React, {useState, useEffect} from 'react';
import {array, bool, func, string} from 'prop-types';
import {get, without} from 'lodash';

import { makeStyles, withStyles } from '@material-ui/core';
import Paper from '@material-ui/core/Paper';
import UITable from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Checkbox from '@material-ui/core/Checkbox';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import DuplicateIcon from '@material-ui/icons/FileCopy';

import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';

import './Table.css';

const ToolbarButton = withStyles(() => ({
  root: {
    color: '#000',
  }
}))(IconButton);

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  container: {
    height: '100%',
  },
});

export default function Table({
  columns, sortBy, sortOrder, onSortClick, items, deleteButton = true, selectDisabled = false, duplicateButton = false, onEdit, onDelete, onDuplicate
}) {
  const classes = useStyles();

  const [itemsSelected, setItemsSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [confirmDialogTarget, setConfirmDialogTarget] = useState(null);
  const [confirmDialogActive, setConfirmDialogActive] = useState(false);

  useEffect(() => {
    setItemsSelected([]);
    setConfirmDialogActive(false);
  }, [items])

  function handleAllItemsCheckChange() {
    if (itemsSelected.length < items.length) {
      setItemsSelected(items.map(item => item.id));
    } else {
      setItemsSelected([]);
    }
  }

  function handleItemCheckChange(id, checked) {
    setItemsSelected(itemsSelected.includes(id) ? without(itemsSelected, id) : itemsSelected.concat([id]));
  }

  function handleDeleteClick() {
    setConfirmDialogTarget('delete');
    setConfirmDialogActive(true);
  }

  function handleConfirmClick() {
    if (confirmDialogTarget === 'delete') {
      return onDelete(itemsSelected);
    }
    onDuplicate(itemsSelected);
  }

  return (
    <div>
      {itemsSelected.length && !selectDisabled
        ? <div className="Table-header">
          <div className="Table-header-itemsSelected">{`${itemsSelected.length} items selected`}</div>
          <div className="Table-header-actions">
            {itemsSelected.length === 1 && onEdit
              ? <ToolbarButton
                aria-label="edit-item"
                onClick={() => onEdit(itemsSelected[0])}
              >
              <EditIcon />
            </ToolbarButton>
            : null}
            {duplicateButton
              ? <ToolbarButton
                aria-label="duplicate-item"
                onClick={() => onDuplicate(itemsSelected)}
              >
              <DuplicateIcon />
            </ToolbarButton>
            : null}
            {deleteButton ?
              <ToolbarButton aria-label="remove-item" onClick={handleDeleteClick}>
                <DeleteIcon />
              </ToolbarButton>
            : null}
          </div>
        </div>
      : null}
      <Paper className={classes.root}>
        <TableContainer className={classes.container}>
          <UITable stickyHeader>
            <TableHead>
              <TableRow>
                <>
                  {!selectDisabled
                    ? <TableCell padding="checkbox" style={{width: '40px'}}>
                      <Checkbox
                        checked={itemsSelected.length === items.length && items.length > 0}
                        onChange={handleAllItemsCheckChange}
                      />
                    </TableCell>
                    : null}
                  {columns.map((column) => (
                    <TableCell key={column.id} align={column.align} style={{width: column.minWidth}}>
                      {column.sort ?
                        <TableSortLabel active={sortBy === column.id} direction={sortOrder} onClick={() => onSortClick(column.id)}>
                          {column.label}
                        </TableSortLabel>
                        : column.label}
                    </TableCell>
                  ))}
                </>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(row => (
                <TableRow
                  key={row.id}
                  hover
                  role="checkbox"
                  tabIndex={-1}
                  selected={itemsSelected.includes(row.id)}
                  // onClick={(ev, checked) => handleItemCheckChange(row.id, checked)}
                >
                  <>
                    {!selectDisabled
                      ? <TableCell padding="checkbox" style={{width: '40px'}}>
                        <Checkbox
                          checked={itemsSelected.includes(row.id)}
                          onChange={(ev, checked) => handleItemCheckChange(row.id, checked)}
                        />
                      </TableCell>
                      : null}
                    {columns.map((column) => {
                      const value = get(row, column.id);
                      return (
                        <TableCell key={column.id} align={column.align}>
                          {column.format
                            ? column.format(value)
                            : column.link ? <a href={value}>{value}</a> : value}
                        </TableCell>
                      )
                    })}
                  </>
                </TableRow>
              ))}
            </TableBody>
          </UITable>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={items.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onChangePage={(ev, newPage) => setPage(newPage)}
          onChangeRowsPerPage={(ev, newRowsPerPage) => setRowsPerPage(ev.target.value)}
        />
      </Paper>
      <ConfirmDialog
        message={`Are you sure you want to delete ${itemsSelected.length === 1 ? 'this item' : 'those items'}?`}
        active={confirmDialogActive}
        onClose={() => setConfirmDialogActive(false)}
        onConfirm={handleConfirmClick}
      />
    </div>
  )
}

Table.propTypes = {
  columns: array,
  items: array,
  sortBy: string,
  sortOrder: string,
  selectDisabled: bool,
  onEdit: func,
  onDuplicate: func,
  onDelete: func,
  onSortClick: func,
  duplicateButton: bool,
  deleteButton: bool
}
